/**
 * Verwaltungsbereiche: welche Reiter der AdminView unter welchem Menüpunkt
 * stehen (Menü-Umbau 19.10.2026).
 *
 * Vorher renderten vier Menüpunkte (`admin`, `admin-projects`,
 * `project-management`, `users`) dieselbe AdminView mit allen 18 Reitern;
 * nur der Startreiter unterschied sich. „Projektverwaltung" sah deshalb aus
 * wie die ganze Admin-Verwaltung. Jetzt zeigt jeder Menüpunkt nur seine
 * eigenen Reiter — die Tabelle hier ist die EINE Stelle, an der das steht.
 *
 * Rein: kein React, kein Supabase. App.tsx (Rendern, Standardreiter),
 * AdminView (Reiterleiste, Titel) und Sidebar lesen von hier.
 */
import type { AppView } from '../../types';

/** Jeder Reiter, den die AdminView rendern kann. */
export type AdminTab =
    | 'dashboard'
    | 'projects' | 'alerts' | 'sent-reports' | 'transfers' | 'deletion-requests'
    | 'users' | 'rollen' | 'user-activity'
    | 'settings' | 'settings-angebote' | 'settings-rechnungen'
    | 'catalog' | 'maengelkatalog' | 'plotter-vorlagen' | 'reference-sheet' | 'project-images'
    | 'open-positions'
    | 'substitutions'
    | 'integrity' | 'bugs' | 'wartung' | 'lernzentrum' | 'api-logs';

/** Die Ansichten, die die AdminView rendern. */
export type AdminBereichView =
    | 'admin'
    | 'admin-projects'
    | 'users'
    | 'admin-settings'
    | 'project-management'
    | 'admin-vorlagen'
    | 'admin-open-positions'
    | 'admin-substitutions';

export interface AdminBereich {
    /** Seitentitel in der AdminView. */
    titel: string;
    /** Reiter in Anzeigereihenfolge; der erste ist der Standardreiter. */
    tabs: readonly AdminTab[];
}

export const ADMIN_BEREICHE: Readonly<Record<AdminBereichView, AdminBereich>> = {
    'admin-projects': {
        titel: 'Projektverwaltung',
        tabs: ['projects', 'alerts', 'sent-reports', 'transfers', 'deletion-requests'],
    },
    users: {
        titel: 'Benutzer & Rechte',
        tabs: ['users', 'rollen', 'user-activity'],
    },
    'admin-settings': {
        titel: 'Firmeneinstellungen',
        tabs: ['settings', 'settings-angebote', 'settings-rechnungen'],
    },
    // `admin` ist seit dem Umbau der Bereich „System". Der Wert bleibt, damit
    // ein gemerkter Zustand (navigationPersistence) nicht ins Leere führt.
    admin: {
        titel: 'System',
        tabs: ['integrity', 'bugs', 'wartung', 'lernzentrum', 'api-logs'],
    },
    'project-management': {
        titel: 'Auswertungen',
        tabs: ['dashboard'],
    },
    'admin-vorlagen': {
        titel: 'Kataloge & Vorlagen',
        tabs: ['catalog', 'maengelkatalog', 'plotter-vorlagen', 'reference-sheet', 'project-images'],
    },
    'admin-open-positions': {
        titel: 'Offene Positionen',
        tabs: ['open-positions'],
    },
    'admin-substitutions': {
        titel: 'Vertretungen',
        tabs: ['substitutions'],
    },
};

export const ADMIN_BEREICH_VIEWS: readonly AdminBereichView[] =
    Object.keys(ADMIN_BEREICHE) as AdminBereichView[];

export const istAdminBereichView = (view: AppView | string): view is AdminBereichView =>
    Object.prototype.hasOwnProperty.call(ADMIN_BEREICHE, view);

/** Standardreiter eines Bereichs (der erste). */
export const ersterTab = (view: AdminBereichView): AdminTab => ADMIN_BEREICHE[view].tabs[0];

/**
 * Der Reiter, der in diesem Bereich wirklich gezeigt wird: der gewünschte,
 * wenn er dazugehört, sonst der Standardreiter. Fängt gemerkte Werte von vor
 * dem Umbau ab (`admin.tab` = 'projects' unter `admin`, 'settings' aus der
 * alten Konsole, 'invites', 'api' …).
 */
export const tabImBereich = (view: AdminBereichView, tab: string | null | undefined): AdminTab => {
    const tabs = ADMIN_BEREICHE[view].tabs as readonly string[];
    return tab && tabs.includes(tab) ? (tab as AdminTab) : ersterTab(view);
};
